import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { login, requestPasswordReset } from '../../services/pocketbase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiAlertCircle,
  FiCheckCircle,
  FiArrowLeft,
  FiShield,
  FiCheck
} = FiIcons;

const Login = ({ isAdminLogin = false }) => {
  const { handleLogin } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const authData = await login(formData.email, formData.password);
      const userData = await handleLogin(authData);

      if (userData.role === 'bpas_admin' || userData.role === 'super_admin') {
        window.location.hash = '#/admin/tenants';
      } else {
        window.location.hash = '#/dashboard';
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setResetSuccess('');

    try {
      await requestPasswordReset(formData.email);
      setResetSuccess('Password reset link has been sent to your email.');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const toggleResetMode = () => {
    setIsResetMode(!isResetMode);
    setError('');
    setResetSuccess('');
  };

  const eyebrow = isResetMode
    ? 'Account recovery'
    : isAdminLogin
      ? 'BPAS team access'
      : 'Client access';

  const heading = isResetMode
    ? 'Reset your password'
    : isAdminLogin
      ? 'BPAS team sign in'
      : 'Welcome back';

  const subtitle = isResetMode
    ? 'Enter your email address and we will send you a secure password reset link.'
    : isAdminLogin
      ? 'Sign in to administer tenants, users and platform requests.'
      : 'Sign in to manage your enquiries, website activity and business information.';

  const features = [
    'Tenant-focused client workspace',
    'Enquiries and follow-ups in one place',
    'Help and support available inside the hub'
  ];

  return (
    <div className="theme-login-shell min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="theme-login-frame"
      >
        <section className="theme-login-brand" aria-label="BPAS Client Hub introduction">
          <div className="theme-login-brand-content">
            <div className="theme-login-brand-mark mb-10">BPAS</div>
            <p className="theme-login-kicker text-xs font-bold mb-4">Business Process Automation Solutions</p>
            <h1 className="theme-login-brand-heading text-4xl lg:text-5xl font-bold leading-tight max-w-md">
              Your client operations, organised in one workspace.
            </h1>
            <p className="theme-login-brand-copy mt-5 text-base leading-7 max-w-md">
              A focused portal for managing enquiries, website requests, business information and support without unnecessary complexity.
            </p>

            <div className="theme-login-brand-features mt-10 space-y-4">
              {features.map((feature) => (
                <div key={feature} className="theme-login-feature flex items-center gap-3 text-sm font-medium">
                  <span className="theme-login-feature-icon w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  </span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="theme-login-brand-footer pt-10 text-xs font-medium tracking-wide">
            BPA Solutions · Client Hub
          </div>
        </section>

        <section className="theme-login-form-panel">
          <div className="theme-login-form-inner">
            <div className="mb-8">
              <div className="theme-login-eyebrow mb-5">
                <SafeIcon icon={FiShield} className="w-3.5 h-3.5" />
                <span>{eyebrow}</span>
              </div>
              <h2 className="theme-login-heading text-3xl sm:text-4xl font-bold mb-3">{heading}</h2>
              <p className="theme-login-subtitle text-sm sm:text-base leading-6">{subtitle}</p>
            </div>

            <form onSubmit={isResetMode ? handleResetSubmit : handleSubmit} className="space-y-5">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="theme-login-alert border rounded-xl p-4 flex items-start gap-3"
                  role="alert"
                >
                  <SafeIcon icon={FiAlertCircle} className="theme-login-alert-icon w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="text-sm leading-5">{error}</span>
                </motion.div>
              )}

              {resetSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="theme-login-alert border rounded-xl p-4 flex items-start gap-3"
                  role="status"
                >
                  <SafeIcon icon={FiCheckCircle} className="theme-login-alert-icon w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span className="text-sm leading-5">{resetSuccess}</span>
                </motion.div>
              )}

              <div>
                <label htmlFor="login-email" className="block text-sm font-semibold text-gray-800 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    className="theme-login-field w-full pl-12 pr-4 py-3.5 border rounded-xl transition-all"
                    placeholder="name@company.com"
                  />
                </div>
              </div>

              {!isResetMode && (
                <div>
                  <div className="flex items-center justify-between mb-2 gap-4">
                    <label htmlFor="login-password" className="block text-sm font-semibold text-gray-800">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={toggleResetMode}
                      className="theme-login-link text-sm font-semibold transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      autoComplete="current-password"
                      className="theme-login-field w-full pl-12 pr-12 py-3.5 border rounded-xl transition-all"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="theme-login-subtitle text-xs mt-2">Email address and password are case sensitive.</p>
                </div>
              )}

              <motion.button
                whileHover={{ y: -1 }}
                whileTap={{ y: 0 }}
                type="submit"
                disabled={isLoading}
                className="theme-login-primary-button w-full py-3.5 px-4 rounded-xl font-bold focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isLoading
                  ? isResetMode
                    ? 'Sending reset link...'
                    : 'Signing in...'
                  : isResetMode
                    ? 'Send reset link'
                    : 'Sign in'}
              </motion.button>

              {isResetMode && (
                <button
                  type="button"
                  onClick={toggleResetMode}
                  className="theme-login-link w-full flex items-center justify-center gap-2 text-sm font-semibold py-2 transition-colors"
                >
                  <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                  <span>Back to sign in</span>
                </button>
              )}
            </form>

            {!isResetMode && (
              <div className="mt-8 pt-6 border-t theme-login-divider">
                {!isAdminLogin ? (
                  <p className="theme-login-subtitle text-sm text-center">
                    BPA Solutions team member?{' '}
                    <a href="#/admin-login" className="theme-login-link font-bold transition-colors">
                      Admin login
                    </a>
                  </p>
                ) : (
                  <p className="theme-login-subtitle text-sm text-center">
                    Client user?{' '}
                    <a href="#/login" className="theme-login-link font-bold transition-colors">
                      Client login
                    </a>
                  </p>
                )}
              </div>
            )}

            <div className="mt-7 text-center">
              <p className="theme-login-subtitle text-xs">
                {isAdminLogin ? 'A BPA Solutions platform' : 'Powered by BPA Solutions'}
              </p>
            </div>
          </div>
        </section>
      </motion.div>
    </div>
  );
};

export default Login;